import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismInput, PrismRadioGroup } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';

const CANCELLATION_POLICIES = {
  FREE_CANCELLATION_MAPPING: 'free_cancellation',
  CANCELLATION_COST_MAPPING: 'cancellation_cost',
  NON_REFUNDABLE_MAPPING: 'non_refundable',
};

interface ParityParityManagerBookingDetailsFormSignature {
  Args: {
    back: unknown;
    issue: NewIssue;
    next: unknown;
    setIssueProperty: unknown;
    setIssuePropertyFromEvent: unknown;
  };
}

export default class ParityParityManagerBookingDetailsForm extends Component<ParityParityManagerBookingDetailsFormSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bookingId() {
    return this.args.issue?.booking_id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get firstName() {
    return this.args.issue?.guest_first_name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    return this.firstName && this.lastName && this.bookingId;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lastName() {
    return this.args.issue?.guest_last_name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDate() {
    return this.hotelAndMarketService.getLocalTime();
  }

  @action
  // @ts-expect-error: Incorrect type
  onSelectionCancelDate(value): void {
    if (value) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.setIssueProperty('cancel_before', value.format('YYYY-MM-DD'));
    } else {
      // @ts-expect-error: Incorrect type
      this.args.setIssueProperty('cancel_before', value);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  setSelectedChannelCancellationPolicy(event): void {
    // @ts-expect-error: Incorrect type
    this.args.setIssueProperty(
      'channel_cancellation_policy',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      event.target.value,
    );
  }

  <template>
    <div class="pt-400 pr-500 pb-800 pl-500" id="add-issue-form">
      <h2 class="header-title">{{t
          "parity_manager.add_issue.booking_details"
        }}</h2>
      <div class="mt-400 flex flex-row">
        <div class="flex w-1/2 flex-col pr-400">
          <PrismInput
            @label={{t "general.first_name"}}
            {{! @glint-expect-error: Incorrect type }}
            @onInput={{fn @setIssuePropertyFromEvent "guest_first_name"}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @value={{@issue.guest_first_name}}
          />
        </div>
        <div class="flex w-1/2 flex-col">
          <PrismInput
            @label={{t "general.last_name"}}
            {{! @glint-expect-error: Incorrect type }}
            @onInput={{fn @setIssuePropertyFromEvent "guest_last_name"}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @value={{@issue.guest_last_name}}
          />
        </div>
      </div>
      <div class="flex w-full flex-col">
        <PrismInput
          @label={{t "parity_manager.booking_id"}}
          {{! @glint-expect-error: Incorrect type }}
          @onInput={{fn @setIssuePropertyFromEvent "booking_id"}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{@issue.booking_id}}
        />
      </div>
      <h2 class="header-title">{{t
          "parity_manager.test_booking.cancellation"
        }}</h2>
      <div class="mt-200 flex flex-row">
        <div class="flex w-1/2 flex-col pr-400">
          <label class="input-label">
            {{t "parity_manager.channel_cancellation_policy"}}
          </label>
          <PrismRadioGroup
            @name="channel-cancellation-policy-radio-group"
            @onChange={{this.setSelectedChannelCancellationPolicy}}
            {{! @glint-expect-error: Incorrect type }}
            @value={{@issue.channel_cancellation_policy}}
            class="mt-100"
            as |RadioGroup|
          >
            <RadioGroup.input
              @value={{CANCELLATION_POLICIES.FREE_CANCELLATION_MAPPING}}
            >
              {{t "parity_manager.free_cancellation"}}
            </RadioGroup.input>
            <RadioGroup.input
              @value={{CANCELLATION_POLICIES.CANCELLATION_COST_MAPPING}}
            >
              {{t "parity_manager.cancellation_cost"}}
            </RadioGroup.input>
            <RadioGroup.input
              @value={{CANCELLATION_POLICIES.NON_REFUNDABLE_MAPPING}}
            >
              {{t "rates.nonrefundable"}}
            </RadioGroup.input>
          </PrismRadioGroup>
        </div>
        <div class="flex w-1/2 flex-col">
          <GeneralFormsFormFieldsDatepickerFormField
            {{! @glint-expect-error: Incorrect type }}
            @datePickerMinDate={{this.minDate}}
            @isOptional={{true}}
            @label={{t "parity_manager.cancel_before"}}
            @onSelectDate={{this.onSelectionCancelDate}}
            @selectedDate={{@issue.cancel_before}}
          />
        </div>
      </div>
    </div>
    <div class="settings-modal__footer flex flex-row justify-end">
      <GeneralFormFieldsCancelAndConfirm
        {{! @glint-expect-error: Incorrect type }}
        @cancel={{@back}}
        @cancelText={{t "general.back"}}
        {{! @glint-expect-error: Incorrect type }}
        @confirm={{@next}}
        @confirmText={{t "general.next_step"}}
        @isConfirmDisabled={{not this.isFormValid}}
      />
    </div>
  </template>
}
